import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useParams } from "react-router";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { IconButton } from "@/components/common/IconButton";
import { captionStyle, pageTitleStyle } from "@/components/infra/infra-styles";
import {
	DAY_WIDTH_MAX,
	DAY_WIDTH_MIN,
	RoadmapGantt,
	type RoadmapGanttHandle,
} from "@/components/roadmap/RoadmapGantt";
import { RoadmapTaskPanel } from "@/components/roadmap/RoadmapTaskPanel";
import {
	createRoadmapTask,
	deleteRoadmapTask,
	getRoadmapAssignees,
	getRoadmapTasks,
	updateRoadmapTask,
	type RoadmapAssignee,
	type RoadmapTask,
	type RoadmapTaskInput,
} from "@/lib/api";
import {
	assigneeIdOfLane,
	buildLanes,
	dateOfIndex,
	taskInput,
	timelineRange,
	todayIndex,
	type DayRange,
} from "@/lib/roadmap";
import { readStoredRoadmapLaneHues, writeStoredRoadmapLaneHues } from "@/lib/roadmap-lane-hues";
import { readStoredRoadmapDayWidth, writeStoredRoadmapDayWidth } from "@/lib/roadmap-zoom";
import { SerialSaves } from "@/lib/serial-saves";

/**
 * ロードマップ (/roadmap、クラウド版の自社組織のユーザーのみ)。社内の計画をガントチャートで管理する。
 * 変更は操作ごとに保存し、失敗したら画面を保存前に戻してエラーを出す。
 */
export function RoadmapPage() {
	const navigate = useNavigate();
	const [tasks, setTasks] = useState<RoadmapTask[] | null>(null);
	const [assignees, setAssignees] = useState<RoadmapAssignee[]>([]);
	// 選択中のタスクは URL に載せ、リンクで直接開けるようにする。
	const selectedId = useParams<{ taskId: string }>().taskId ?? null;
	const setSelectedId = (id: string | null) =>
		navigate(id === null ? "/roadmap" : `/roadmap/${id}`, { replace: true });
	const [error, setError] = useState<string | null>(null);
	const [dayWidth, setDayWidth] = useState(readStoredRoadmapDayWidth);
	const ganttRef = useRef<RoadmapGanttHandle>(null);
	const saves = useRef(new SerialSaves());
	// サーバーが最後に受け付けた値。保存に失敗したらここまで戻す。
	const confirmed = useRef(new Map<string, RoadmapTask>());
	const today = useMemo(() => todayIndex(), []);
	const [laneHues, setLaneHues] = useState(readStoredRoadmapLaneHues);
	const lanes = useMemo(
		() => buildLanes(assignees, tasks ?? [], laneHues),
		[assignees, tasks, laneHues],
	);
	const changeLaneHue = (laneKey: string, hue: number | null) => {
		const next = new Map(laneHues);
		if (hue === null) next.delete(laneKey);
		else next.set(laneKey, hue);
		setLaneHues(next);
		writeStoredRoadmapLaneHues(next);
	};

	useEffect(() => {
		let active = true;
		Promise.all([getRoadmapTasks(), getRoadmapAssignees()])
			.then(([loadedTasks, loadedAssignees]) => {
				if (!active) return;
				for (const task of loadedTasks) confirmed.current.set(task.id, task);
				setTasks(loadedTasks);
				setAssignees(loadedAssignees);
			})
			.catch((err: Error) => {
				if (active) setError(err.message);
			});
		return () => {
			active = false;
		};
	}, []);

	// 表示期間は読み込み時に決め、タスクの移動でスクロール位置がずれないようにする。
	const [range, setRange] = useState<DayRange | null>(null);
	if (tasks !== null && range === null) setRange(timelineRange(tasks, today));

	const fail = (err: unknown) => {
		console.error(err);
		setError(err instanceof Error ? err.message : String(err));
	};

	const create = async (span: DayRange, laneKey: string, name: string) => {
		try {
			const task = await createRoadmapTask({
				name,
				description: "",
				assigneeUserId: assigneeIdOfLane(laneKey),
				startDate: dateOfIndex(span.start),
				endDate: dateOfIndex(span.end),
				done: false,
			});
			setError(null);
			confirmed.current.set(task.id, task);
			setTasks((prev) => [...(prev ?? []), task]);
			setSelectedId(task.id);
		} catch (err) {
			fail(err);
			throw err;
		}
	};

	const replace = (task: RoadmapTask) =>
		setTasks((prev) => prev?.map((t) => (t.id === task.id ? task : t)) ?? null);

	// 画面には先に反映し、同じタスクの保存は順に送る。結果は最後に頼んだ保存のものだけ画面に戻す。
	const save = (id: string, input: RoadmapTaskInput) => {
		replace({ id, ...input });
		void saves.current
			.enqueue(id, () => updateRoadmapTask(id, input))
			.then((outcome) => {
				if (outcome.ok) {
					confirmed.current.set(id, outcome.value);
					if (outcome.latest) {
						setError(null);
						replace(outcome.value);
					}
					return;
				}
				fail(outcome.error);
				const last = confirmed.current.get(id);
				if (outcome.latest && last !== undefined) replace(last);
			});
	};

	const remove = async (id: string) => {
		try {
			await deleteRoadmapTask(id);
		} catch (err) {
			// 削除の確認ダイアログが失敗を表示するので、ここではログだけ残して投げ直す。
			console.error(err);
			throw err;
		}
		confirmed.current.delete(id);
		setTasks((prev) => prev?.filter((t) => t.id !== id) ?? null);
		setSelectedId(null);
	};

	const selected = tasks?.find((t) => t.id === selectedId);

	return (
		<div
			data-testid="roadmap-page"
			style={{ display: "flex", height: "100vh", background: "var(--surface-1)" }}
		>
			<div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 16,
						padding: "24px 32px 16px",
					}}
				>
					<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<IconButton
							icon="arrowL"
							title="Izanagi へ戻る"
							data-testid="roadmap-back"
							onClick={() => navigate("/")}
						/>
						<h1 style={pageTitleStyle}>ロードマップ</h1>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
						<label
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								fontSize: "var(--text-caption)",
								color: "var(--fg-2)",
							}}
						>
							ズーム
							<input
								type="range"
								min={DAY_WIDTH_MIN}
								max={DAY_WIDTH_MAX}
								value={dayWidth}
								disabled={range === null}
								onChange={(e) => {
									const value = Number(e.target.value);
									setDayWidth(value);
									writeStoredRoadmapDayWidth(value);
								}}
								data-testid="roadmap-zoom"
								className="izanagi-slider"
								style={
									{
										width: 120,
										"--slider-track-size": "4px",
										"--slider-thumb-size": "14px",
										"--slider-ratio": (dayWidth - DAY_WIDTH_MIN) / (DAY_WIDTH_MAX - DAY_WIDTH_MIN),
									} as CSSProperties
								}
							/>
						</label>
						<ConfirmButton
							label="今日へ移動"
							variant="outline"
							size="sm"
							loading={false}
							disabled={range === null}
							onClick={() => ganttRef.current?.scrollToToday()}
							data-testid="roadmap-scroll-today"
						/>
					</div>
				</div>
				{error !== null && (
					<p
						data-testid="roadmap-error"
						style={{ margin: "0 32px 12px", fontSize: "var(--text-ui)", color: "var(--danger)" }}
					>
						{error}
					</p>
				)}
				{tasks === null || range === null ? (
					error === null && <p style={captionStyle({ margin: "0 32px" })}>読み込み中…</p>
				) : (
					<div
						style={{ flex: 1, minHeight: 0, display: "flex", borderTop: "1px solid var(--line-1)" }}
					>
						<RoadmapGantt
							ref={ganttRef}
							tasks={tasks}
							lanes={lanes}
							range={range}
							today={today}
							dayWidth={dayWidth}
							selectedId={selectedId}
							onSelect={setSelectedId}
							onCreate={create}
							laneHueChosen={(laneKey) => laneHues.has(laneKey)}
							onLaneHueChange={changeLaneHue}
							onChange={(task, span, laneKey) =>
								save(task.id, {
									...taskInput(task),
									assigneeUserId: assigneeIdOfLane(laneKey),
									startDate: dateOfIndex(span.start),
									endDate: dateOfIndex(span.end),
								})
							}
						/>
					</div>
				)}
			</div>
			{selected !== undefined && range !== null && (
				<RoadmapTaskPanel
					key={selected.id}
					task={selected}
					assignees={assignees}
					range={range}
					onSave={(input) => save(selected.id, input)}
					onDelete={() => remove(selected.id)}
					onClose={() => setSelectedId(null)}
				/>
			)}
		</div>
	);
}
